import type React from "react";

import type { FieldProps } from "../../../types/FormTypes";
import Checkbox from "../Checkbox/Checkbox";
import Field, { useSyntheticFieldRef } from "../Field/Field";
import { useField } from "../Form/Form";
import styles from "./CheckboxField.module.scss";

export interface CheckboxFieldProps extends Omit<FieldProps<boolean>, "error"> {
  size?: "sm" | "md";
  error?: boolean | string;
  endAdornment?: React.ReactNode;
}

const CheckboxField = (props: CheckboxFieldProps) => {
  const {
    name,
    label,
    helperText,
    error,
    required,
    disabled,
    fullWidth = false,
    value: controlledValue,
    onChange,
    onValueChange,
    endAdornment,
    ...otherProps
  } = props;
  const field = useField<boolean>({ name, value: controlledValue, onChange, onValueChange, required });
  const value = typeof field.value === "boolean" ? field.value : false;
  const syntheticFieldRef = useSyntheticFieldRef<HTMLButtonElement, boolean>(
    field.ref as any,
    value,
    (nextValue) => nextValue === true || nextValue === "true",
  );
  const errorMessage = typeof error === "string" ? error : undefined;
  const hasError = Boolean(field.error || errorMessage || (typeof error === "boolean" ? error : false));

  return (
    <Field
      className={styles.checkboxContainer}
      name={name}
      fullWidth={fullWidth}
    >
      <div className={styles.checkboxRow}>
        <Field.Control>
          <Checkbox
            ref={syntheticFieldRef}
            value={value}
            onValueChange={field.onValueChange}
            onBlur={field.onBlur}
            disabled={disabled}
            {...otherProps}
          />
        </Field.Control>
        <div className={styles.checkboxContent}>
          <div className={styles.labelRow}>
            <Field.Label
              required={field.required}
              className={styles.label}
            >
              {label}
            </Field.Label>
            {endAdornment ? <div className={styles.endAdornment}>{endAdornment}</div> : null}
          </div>
          <Field.Message
            error={hasError}
            message={errorMessage ?? helperText}
          />
        </div>
      </div>
    </Field>
  );
};

export default CheckboxField;
